import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftIcon} from "../../../assets/Icons/ArrowLeft";
import { DownloadIcon } from "../../../assets/Icons/DownloadIcon";
import { routes } from "../../../routes/routes.constatns";
import { QualityFrameworkService } from "../../../api/services";
import { createExcelFile } from "../../../util/excelUtil";
import { hasQualityFrameworkAccess } from "../qualityFramework.utils";
import { QualityFrameworkAccessDenied } from "../QualityFrameworkAccessDenied";
import "./CheckList.scss";

const ANSWER_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "NO" },
  { value: "NA", label: "NA" },
];

// Efforts/savings questions carry their value in dedicated fields instead of autoFilledValue
const formatAutoFilledValue = (detail) => {
  if (detail.autoFilledValue != null && detail.autoFilledValue !== "") {
    return detail.autoFilledValue;
  }
  if (detail.totalEfforts != null || detail.totalSavings != null) {
    return `Efforts: ${detail.totalEfforts ?? "-"} | Savings: ${detail.totalSavings ?? "-"}`;
  }
  return "-";
};

export const CheckList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRoles = useSelector((state) => state.user?.userRoles);
  const rolesLoaded = useSelector((state) => state.user?.rolesLoaded);
  const hasAccess = hasQualityFrameworkAccess(userRoles);
  const orderRequestId = location.state?.orderRequestId;
  const orderDetails = location.state?.orderDetails || {};
  const checklistSummary = [
    `AX-${orderDetails.axonId ?? orderRequestId ?? ""}`,
    `${orderDetails.clientName || "-"} – ${orderDetails.engagementName || "-"}`,
    orderDetails.countryName || "-",
    orderDetails.analyzerType || "-",
  ].join(" | ");
  const [questions, setQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState("");
  const [answers, setAnswers] = useState({});
  const [answerDetails, setAnswerDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isReadOnly, setIsReadOnly] = useState(
    location.state?.status?.toLowerCase() === "completed",
  );

  useEffect(() => {
    let isActive = true;

    if (!rolesLoaded || !hasAccess) {
      return undefined;
    }

    const loadQuestions = async () => {
      try {
        setIsLoadingQuestions(true);
        setQuestionsError("");
        const response = await QualityFrameworkService.getChecklistQuestions();
        if (isActive) {
          setQuestions(response?.data || response || []);
        }
      } catch (error) {
        console.error("Error fetching checklist questions:", error);
        if (isActive) {
          setQuestions([]);
          setQuestionsError("Unable to load checklist questions.");
        }
      } finally {
        if (isActive) {
          setIsLoadingQuestions(false);
        }
      }
    };

    loadQuestions();

    return () => {
      isActive = false;
    };
  }, [rolesLoaded, hasAccess]);

  useEffect(() => {
    let isActive = true;

    if (!orderRequestId || !rolesLoaded || !hasAccess) {
      return undefined;
    }

    const loadAnswers = async () => {
      try {
        const response =
          await QualityFrameworkService.getChecklistAnswers(orderRequestId);
        const savedCategories = response?.data?.categories || [];
        const savedItems = savedCategories.flatMap((cat) => cat.items || []);

        if (!isActive) return;

        const answerMap = {};
        const detailMap = {};

        savedItems.forEach((item) => {
          answerMap[item.questionId] = {
            answer: item.response ?? "",
            comment: item.comment ?? "",
          };
          detailMap[item.questionId] = item;
        });

        setAnswers(answerMap);
        setAnswerDetails(detailMap);
        setIsReadOnly(Boolean(response?.data?.isSubmitted));
      } catch (error) {
        console.error("Error fetching checklist answers:", error);
      }
    };

    loadAnswers();

    return () => {
      isActive = false;
    };
  }, [orderRequestId, rolesLoaded, hasAccess]);

  // Grouped by category in sortOrder, with a question number running across categories
  const categoryGroups = useMemo(() => {
    const sorted = [...questions].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );

    const groups = [];
    const groupByCategory = new Map();
    let displayNumber = 0;

    sorted.forEach((question) => {
      const category = question.category || "Others";
      if (!groupByCategory.has(category)) {
        const group = { category, questions: [] };
        groupByCategory.set(category, group);
        groups.push(group);
      }
      displayNumber += 1;
      groupByCategory
        .get(category)
        .questions.push({ ...question, displayNumber });
    });

    return groups;
  }, [questions]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        answer: value,
      },
    }));
  };

  const handleCommentChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], comment: value },
    }));
  };

  const isCommentEnabled = (question) =>
    Boolean(answers[question.questionId]?.answer);

  const handleSubmit = async () => {
    const missingAnswers = questions.filter((question) => {
      if (question.isAutoFilled) return false;
      const entry = answers[question.questionId] || {};
      if (question.isMandatory && !entry.answer) return true;
      return false;
    });

    const missingComments = questions.filter((question) => {
      if (question.isAutoFilled) return false;
      const entry = answers[question.questionId] || {};
      return entry.answer === "No" && !entry.comment?.trim();
    });

    if (missingAnswers.length || missingComments.length) {
      const validationMessages = [];
      if (missingAnswers.length) {
        validationMessages.push("Please answer all mandatory questions.");
      }
      if (missingComments.length) {
        const questionIds = missingComments
          .map((question) => question.questionId)
          .join(", ");
        validationMessages.push(
          `Comments are required for all questions marked "No" ( Question ID(s): ${questionIds}).`,
        );
      }
      setSubmitError(validationMessages.join(" "));
      return;
    }

    const payload = {
      orderRequestId,
      responses: questions
        .filter((question) => {
          const detail = answerDetails[question.questionId] || {};
          return !(detail.isAutoFilled ?? question.isAutoFilled);
        })
        .map((question) => {
          const entry = answers[question.questionId] || {};
          return {
            qId: question.questionId,
            response: entry.answer || "",
            comment: entry.comment?.trim() || "",
            isAutoFilled: false,
          };
        }),
    };

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await QualityFrameworkService.submitChecklistAnswers(payload);
      navigate(`/${routes.qualityFramework}`);
    } catch (error) {
      console.error("Error submitting checklist answers:", error);
      setSubmitError("Unable to submit the checklist. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadResponse = async () => {
    const rows = categoryGroups.flatMap((group) =>
      group.questions.map((question) => {
        const detail = answerDetails[question.questionId] || {};
        const isAutoFilled = detail.isAutoFilled ?? question.isAutoFilled;
        const entry = answers[question.questionId] || {};

        return {
          "Question ID": question.questionId,
          Category: group.category,
          Question: question.questionText || "",
          Answer: isAutoFilled
            ? String(formatAutoFilledValue(detail))
            : entry.answer || "",
          Comments: isAutoFilled ? "" : entry.comment || "",
        };
      }),
    );

    if (!rows.length) return;

    try {
      const exportBlob = await createExcelFile([
        { name: "IQM Checklist", data: rows },
      ]);

      if (!exportBlob) return;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(exportBlob);
      link.download = `iqm-checklist-${orderRequestId ?? "response"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error exporting checklist response:", error);
    }
  };

  if (rolesLoaded && !hasAccess) {
    return <QualityFrameworkAccessDenied />;
  }

  return (
    <div className="check-list-page" data-testid="check-list-page">
      <div className="check-list-header">
        <button
          type="button"
          className="check-list-back-btn"
          onClick={() => navigate(`/${routes.qualityFramework}`)}
          aria-label="Back to Quality Framework"
        >
          <ArrowLeftIcon height={16} width={16} color="#dedee2" /> Back
        </button>
        <h1 className="check-list-title">IQM Checklist</h1>

        <div className="check-list-header-actions">
          <button
            type="button"
            className="check-list-secondary-btn"
            onClick={handleDownloadResponse}
            disabled={!isReadOnly}
          >
            <DownloadIcon width={16} height={16} color="#f0f2f8" />
            Download Response
          </button>
          {!isReadOnly && (
            <button
              type="button"
              className="check-list-primary-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Response"}
            </button>
          )}
        </div>
      </div>
      <div className="check-list-order-summary" data-testid="check-list-order-summary">
        {checklistSummary}
      </div>
      {submitError && (
        <div className="check-list-status check-list-status-error">
          {submitError}
        </div>
      )}
      {isLoadingQuestions && (
        <div className="check-list-status">Loading checklist questions...</div>
      )}
      {!isLoadingQuestions && questionsError && (
        <div className="check-list-status check-list-status-error">
          {questionsError}
        </div>
      )}
      {!isLoadingQuestions && !questionsError && questions.length === 0 && (
        <div className="check-list-status">No checklist questions found.</div>
      )}
      {!isLoadingQuestions && !questionsError && categoryGroups.length > 0 && (
        <div className="check-list-content">
          {categoryGroups.map((group) => (
            <section className="check-list-category" key={group.category}>
              <h2 className="check-list-category-title">{group.category}</h2>

              <div className="check-list-questions">
                {group.questions.map((question) => {
                  const detail = answerDetails[question.questionId] || {};
                  const isAutoFilled =
                    detail.isAutoFilled ?? question.isAutoFilled;
                  const commentEnabled = isCommentEnabled(question);
                  const selectedAnswer =
                    answers[question.questionId]?.answer || "";

                  return (
                    <div
                      className="check-list-question"
                      key={question.questionId}
                    >
                      <p className="check-list-question-text">
                        {question.displayNumber}. {question.questionText}
                        {question.isMandatory && !isAutoFilled && (
                          <span className="check-list-required">*</span>
                        )}
                      </p>

                      <div className="check-list-answer-row">
                        {isAutoFilled ? (
                          <div className="check-list-autofilled">
                            {formatAutoFilledValue(detail)}
                          </div>
                        ) : (
                          <>
                            <div
                              className="check-list-options"
                              role="radiogroup"
                              aria-label={question.questionText}
                            >
                              {ANSWER_OPTIONS.map((option) => (
                                <label
                                  className="check-list-option"
                                  key={option.value}
                                >
                                  <input
                                    type="radio"
                                    name={`question-${question.questionId}`}
                                    value={option.value}
                                    checked={selectedAnswer === option.value}
                                    disabled={isReadOnly}
                                    onChange={() =>
                                      handleAnswerChange(
                                        question.questionId,
                                        option.value,
                                      )
                                    }
                                  />
                                  <span>{option.label}</span>
                                </label>
                              ))}
                            </div>

                            <textarea
                              className="check-list-comment"
                              placeholder={
                                commentEnabled
                                  ? selectedAnswer === "No"
                                    ? "Comment required for No"
                                    : "Comments (optional)"
                                  : "Select an answer to add comments"
                              }
                              value={
                                answers[question.questionId]?.comment || ""
                              }
                              disabled={isReadOnly || !commentEnabled}
                              onChange={(e) =>
                                handleCommentChange(
                                  question.questionId,
                                  e.target.value,
                                )
                              }
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default CheckList;
